import React, { useEffect, useState, useRef } from "react"; // Import useRef for referencing DOM
import leftArrow from "../assets/icons/Arrow_left.svg";
import rightArrow from "../assets/icons/Arrow_right.svg";

// Interface for Carousel props
interface CarouselProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
  titleAlignment?: "left" | "center" | "right";
  subtitle?: string;
  showTitle?: boolean;
  showDots?: boolean;
  showArrows?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  itemsPerPageConfig?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
    large?: number;
  };
  gap?: string;
  arrowPosition?: "outside" | "inside";
  backgroundColor?: string;
  backgroundImage?: string;
  titleColor?: string;
  accentColor?: string;
  dotActiveColor?: string;
  dotInactiveColor?: string;
}

const Carousel = ({
  children,
  className = "",
  title,
  titleAlignment = "center",
  subtitle,
  showTitle = true,
  showDots = true,
  showArrows = true,
  autoPlay = false,
  autoPlayInterval = 5000,
  itemsPerPageConfig = {
    mobile: 1,
    tablet: 2,
    desktop: 3,
    large: 4,
  },
  gap = "gap-6",
  arrowPosition = "outside",
  backgroundColor = "bg-[#F8F9FB]",
  backgroundImage,
  titleColor = "text-[#05326D]",
  accentColor = "border-[#B5AB28]",
  dotActiveColor = "bg-[#0A2440]",
  dotInactiveColor = "bg-gray-300",
}: CarouselProps) => {
  const [index, setIndex] = useState(0); // Current carousel index
  const [visibleCount, setVisibleCount] = useState(
    itemsPerPageConfig.desktop || 3
  ); // Number of items visible per page
  const childrenArray = React.Children.toArray(children); // Convert children to array

  const [touchStartX, setTouchStartX] = useState<number | null>(null); // Touch start position for swipe
  const [touchEndX, setTouchEndX] = useState<number | null>(null); // Touch end position for swipe

  const carouselRef = useRef<HTMLDivElement>(null); // Ref for carousel container
  const [itemWidth, setItemWidth] = useState(0); // Width of each item
  const gapValue = parseInt(gap.replace(/[^0-9]/g, ""), 10) * 4 || 24; // Convert Tailwind gap class to pixels

  // Update visibleCount based on window size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let newVisibleCount = 3;
      if (width < 640) newVisibleCount = itemsPerPageConfig.mobile || 1;
      else if (width < 768) newVisibleCount = itemsPerPageConfig.tablet || 2;
      else if (width < 1024) newVisibleCount = itemsPerPageConfig.desktop || 3;
      else newVisibleCount = itemsPerPageConfig.large || 4;
      setVisibleCount(newVisibleCount);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [itemsPerPageConfig]);
  
  // Calculate precise width for each item based on container width and gap
  useEffect(() => {
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.offsetWidth;
      const newWidth = (containerWidth - (gapValue * (visibleCount - 1))) / visibleCount;
      setItemWidth(newWidth);
    }
  }, [visibleCount, childrenArray.length, gapValue]);

  // Ensure index doesn't go beyond max allowed
  useEffect(() => {
    const maxIndex = Math.max(0, childrenArray.length - visibleCount);
    if (index > maxIndex) setIndex(maxIndex);
  }, [childrenArray.length, visibleCount, index]);

  // AutoPlay functionality
  useEffect(() => {
    if (!autoPlay || childrenArray.length <= visibleCount) return;
    const interval = setInterval(() => {
      setIndex((prev) => {
        const maxIndex = Math.max(0, childrenArray.length - visibleCount);
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, autoPlayInterval);
    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, childrenArray.length, visibleCount]);

  const maxIndex = Math.max(0, childrenArray.length - visibleCount); // Maximum scroll index

  const handleNext = () => {
    setIndex((prev) => Math.min(prev + 1, maxIndex)); // Next button handler
  };
  const handlePrev = () => {
    setIndex((prev) => Math.max(prev - 1, 0)); // Previous button handler
  };
  
  const handleDotClick = (dotIndex: number) => {
    const newIndex = dotIndex * visibleCount; // Jump to page on dot click
    setIndex(Math.min(newIndex, maxIndex));
  };

  // Touch event handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => setTouchStartX(e.targetTouches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) => setTouchEndX(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;
    if (distance > 50) handleNext();
    if (distance < -50) handlePrev();
    setTouchStartX(null);
    setTouchEndX(null);
  };

  const totalPages = Math.ceil(childrenArray.length / visibleCount); // Total pages for dots
  const currentPage = Math.floor(index / visibleCount); // Current active page

  if (!childrenArray || childrenArray.length === 0) return null; // Return null if no children

  return (
    <div
      className={`py-10 px-4 ${className} ${backgroundImage ? "" : backgroundColor}`}
      style={ backgroundImage ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" } : undefined }
    >
      {/* Title and subtitle section */}
      {showTitle && (title || subtitle) && (
        <div className="relative mb-8 text-center max-w-[var(--max-width)] mx-auto">
          <div className={`relative inline-block w-full`} style={{ textAlign: titleAlignment }}>
            {title && (
              <h1 className={`inline-block relative text-3xl md:text-4xl ${titleColor} mb-4 font-normal`}>
                {title}
                <span className={`absolute right-0 -bottom-2 w-[40px] border-b-[6px] ${accentColor} rounded-[6px]`}></span>
              </h1>
            )}
            {subtitle && (
              <p className={`max-w-3xl mx-auto text-md text-[#05326D] mt-4`}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Carousel container */}
      <div className={`relative max-w-[var(--max-width)] mx-auto flex items-center justify-center z-99 ${gap}`}>
        {/* Left arrow button */}
        {showArrows && childrenArray.length > visibleCount && (
            <button onClick={handlePrev} disabled={index === 0}
                className={`${arrowPosition === 'outside' ? 'hidden sm:block' : 'absolute left-2 z-10'} transition-all duration-300 ease-in-out ${index === 0 ? "opacity-30 cursor-not-allowed" : "opacity-100 cursor-pointer z-99 hover:scale-110 hover:-translate-x-1"}`}>
                <img src={leftArrow} alt="Previous" className="w-8 sm:w-6" />
            </button>
        )}

        {/* Carousel track with items */}
        <div
          ref={carouselRef} // Ref for calculating width
          className="overflow-hidden w-full mt-4"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className={`flex transition-transform duration-500 ease-in-out`}
            style={{
              transform: `translateX(-${index * (itemWidth + gapValue)}px)`, // Slide transform
              gap: `${gapValue}px`, // Gap between items
            }}
          >
            {childrenArray.map((child, i) => (
              <div
                key={i}
                className="w-full"
                style={{
                  flex: `0 0 ${itemWidth}px`, // Fixed width for each item
                  width: `${itemWidth}px`,
                }}
              >
                {child}
              </div>
            ))}
          </div>
        </div>
        
        {/* Right arrow button */}
        {showArrows && childrenArray.length > visibleCount && (
            <button onClick={handleNext} disabled={index >= maxIndex}
                className={`${arrowPosition === 'outside' ? 'hidden sm:block' : 'absolute right-2 z-10'} transition-all duration-300 ease-in-out ${index >= maxIndex ? "opacity-30 cursor-not-allowed" : "opacity-100 cursor-pointer z-99 hover:scale-110 hover:translate-x-1"}`}>
                <img src={rightArrow} alt="Next" className="w-8 sm:w-6" />
            </button>
        )}
      </div>

      {/* Pagination dots */}
      {showDots && childrenArray.length > visibleCount && (
        <div className="flex max-w-[var(--max-width)] mx-auto justify-center mt-12 relative z-99 gap-2 sm:mt-24">
          {Array.from({ length: totalPages }).map((_, dotIndex) => (
            <span
              key={dotIndex}
              onClick={() => handleDotClick(dotIndex)}
              className={`h-1 w-4 rounded-full cursor-pointer transition-colors duration-300 ${
                currentPage === dotIndex ? dotActiveColor : dotInactiveColor
              }`}
            ></span>
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
